import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ResponsiveContainer,
  PieChart,
  Legend,
  Tooltip,
  Pie,
  Surface,
  Symbols,
} from 'recharts';
import { Row, Col } from 'antd';
import { Link } from 'react-router-dom';
import pluralize from 'pluralize';

import { summaryActiveAssignmentsDataSelector } from 'redux/selectors/homeDashboardSelectors';
import { AssignmentSections } from 'features/assignment/enums';
import { useAppSelector } from 'hooks/useAppSelector';
import { AssignmentStatusTypeEnum } from 'api/backofficeApiClient';

import styles from '../../UserHomePage.module.less';

const renderTooltip = (props: any) => {
  if (props.active) {
    return (
      <div className={styles.tooltip}>
        <span
          className={styles.colorBox}
          style={{
            backgroundColor: props.payload[0].payload.fillColor,
          }}
        />
        <span>{`${props.payload[0].name}: ${props.payload[0].value}`}</span>
      </div>
    );
  }

  return null;
};

const renderLegend = (props: any) => {
  const { payload } = props;

  return (
    <ul className={styles.legend}>
      {payload.map((entry: any, index: any) => (
        <li className={styles.legendItem} key={`legend-item-${index}`}>
          <Surface width={25} height={10}>
            <Symbols
              cx={5}
              cy={5}
              type="circle"
              size={80}
              fill={entry.fillColor}
            />
            <line x1="10" y1="5" x2="20" y2="5" stroke={entry.fillColor} />
          </Surface>

          <span className={styles.legendName}>{entry.name}</span>
        </li>
      ))}
    </ul>
  );
};

export default function ActiveAssignmentsCard() {
  const [isInitialAnimationActive, setIsInitialAnimationActive] =
    useState<boolean>(true);
  const summaryActiveAssignmentsData = useAppSelector(
    summaryActiveAssignmentsDataSelector,
  );
  const assignmentTotalCount =
    summaryActiveAssignmentsData?.activeAssignmentTotalCount || 0;
  const formattedChartData = useMemo(
    () => [
      {
        name: 'W2',
        value: summaryActiveAssignmentsData?.w2TotalCount || 0,
        fill: 'url(#gradW2)',
        fillColor: '#FF7C74',
      },
      {
        name: 'IC',
        value: summaryActiveAssignmentsData?.icTotalCount || 0,
        fill: 'url(#gradIC)',
        fillColor: '#289AFF',
      },
      {
        name: 'Subcontract',
        value: summaryActiveAssignmentsData?.subcontractTotalCount || 0,
        fill: 'url(#gradSubcontract)',
        fillColor: '#FFC774',
      },
    ],
    [summaryActiveAssignmentsData],
  );
  const isDisable = !formattedChartData.reduce(
    (sum, obj) => sum + obj.value,
    0,
  );
  const [activeIndex, setActiveIndex] = useState<number>();
  const chartId = useRef(`chart-id-${Date.now()}`);
  const animationDuration = 1500;
  const pieDefaultProps = {
    dataKey: 'value',
    animationDuration,
    cx: '25%',
    width: 192,
    height: 192,
    innerRadius: 50,
    outerRadius: 96,
  };

  useEffect(() => {
    setTimeout(() => {
      setIsInitialAnimationActive(false);
    }, animationDuration + 100);
  }, []);

  const onPieEnter = (data: any, index: number) => {
    setActiveIndex(index);
  };

  return (
    <div className={styles.chartWrapper}>
      <Row gutter={[10, 10]}>
        <Col xs={15}>
          <div className={styles.title}>
            <Link to="/my-active-assignments-details">
              My Active Assignments
            </Link>
          </div>
        </Col>
        <Col xs={9}>
          <div className={styles.title}>
            <Link
              to={`/assignments?section=${AssignmentSections.AllAssignments}&status=${AssignmentStatusTypeEnum.Active}`}
            >
              {pluralize('assignment', assignmentTotalCount, true)}
            </Link>
          </div>
        </Col>
      </Row>

      <div
        className={styles.pieChartWrapper}
        style={{
          pointerEvents: isInitialAnimationActive ? 'none' : 'auto',
        }}
      >
        <ResponsiveContainer width="100%" height={242}>
          <PieChart>
            <defs>
              <radialGradient
                id="gradW2"
                gradientUnits="userSpaceOnUse"
                cx="25%"
                r="100%"
              >
                <stop offset="15%" stopColor="#FF9D97" />
                <stop offset="25%" stopColor="#FF7A72" />
              </radialGradient>
              <radialGradient
                id="gradIC"
                gradientUnits="userSpaceOnUse"
                cx="25%"
                r="100%"
              >
                <stop offset="15%" stopColor="#72C6FF" />
                <stop offset="25%" stopColor="#2698FF" />
              </radialGradient>
              <radialGradient
                id="gradSubcontract"
                gradientUnits="userSpaceOnUse"
                cx="25%"
                r="100%"
              >
                <stop offset="15%" stopColor="#FFDAA3" />
                <stop offset="25%" stopColor="#FFC672" />
              </radialGradient>
              <filter
                id="shadow"
                x="0"
                y="0"
                width="150%"
                height="150%"
                filterUnits="userSpaceOnUse"
              >
                <feDropShadow
                  dx="0"
                  dy="0"
                  stdDeviation="3"
                  floodColor="#4a6778"
                />
              </filter>
            </defs>

            {isDisable && (
              <Pie
                data={[
                  {
                    value: 1,
                    fill: '#D8D8D8',
                  },
                ]}
                {...pieDefaultProps}
              />
            )}

            {!isDisable && (
              <Pie
                activeIndex={activeIndex}
                data={formattedChartData}
                onMouseEnter={onPieEnter}
                onMouseLeave={() => setActiveIndex(undefined)}
                {...pieDefaultProps}
              />
            )}

            <Legend
              payload={formattedChartData as any}
              wrapperStyle={{
                width: '35%',
                top: '18px',
                overflow: 'auto',
              }}
              align="right"
              verticalAlign="middle"
              content={renderLegend}
            />

            {!isDisable && <Tooltip content={renderTooltip} />}

            <g>
              <use
                href={`#${chartId.current}`}
                style={{
                  pointerEvents: 'none',
                }}
              />
            </g>
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
